import { useMutation } from "@tanstack/react-query";
import { AlertCircle, CheckCircle, File, Loader2, Upload, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadDocument } from "@/api/documentApi";
import UploadHeader from "@/features/upload/components/UploadHeader";
import useUploadOptions from "@/features/upload/hooks/useUploadOptions";
import { useTheme } from "@/hooks/useTheme";
import type { FileValidationResult, UploadPageProps } from "@/types";
import { formatFileSize, validateFile } from "@/utils/fileValidation";

const UploadPage: React.FC<UploadPageProps> = ({
	onBack: _onBack,
	onViewAnnotation: _onViewAnnotation,
	onViewAnnotationDirectly: _onViewAnnotationDirectly,
}) => {
	useTheme();

	const navigate = useNavigate();
	const location = useLocation();

	const handleBack = useCallback(() => {
		navigate("/app");
	}, [navigate]);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [validationResult, setValidationResult] = useState<FileValidationResult | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	// Check if file was passed from modal
	useEffect(() => {
		if (location.state?.file instanceof File) {
			const file = location.state.file;
			const validation = validateFile(file);
			setValidationResult(validation);
			setSelectedFile(validation.valid ? file : null);
		}
	}, [location.state]);
	const { uploadOptions, toggleOption } = useUploadOptions();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Upload mutation
	const uploadMutation = useMutation({
		mutationFn: (file: File) => uploadDocument(file, uploadOptions),
		onSuccess: (data) => {
			// Clear file from upload section and reset to neutral state
			setSelectedFile(null);
			setValidationResult(null);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			// Navigate to results page with job ID
			navigate(`/results/${data.jobId}`);
		},
	});

	const handleFileSelect = useCallback((file: File) => {
		const validation = validateFile(file);
		setValidationResult(validation);
		setSelectedFile(validation.valid ? file : null);
	}, []);

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(false);

			const file = e.dataTransfer.files[0];
			if (file) {
				handleFileSelect(file);
			}
		},
		[handleFileSelect],
	);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleRemoveFile = () => {
		setSelectedFile(null);
		setValidationResult(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleUpload = () => {
		if (selectedFile && validationResult?.valid) {
			uploadMutation.mutate(selectedFile);
		}
	};

	const handleBrowseClick = () => {
		fileInputRef.current?.click();
	};

	return (
		<div className="min-h-screen surface-alt text-primary">
			<UploadHeader onBack={handleBack} />

			<main className="max-w-3xl mx-auto px-6 py-12">
				<div className="surface rounded-xl shadow-sm border border-subtle p-10">
					{/* Upload Area */}
					{/* biome-ignore lint/a11y/useSemanticElements: Drag-and-drop area requires div with role */}
					<div
						role="button"
						tabIndex={0}
						aria-label="Drag and drop area for file upload"
						className={`border-2 border-dashed rounded-xl p-16 text-center transition-all duration-200 ${
							isDragging
								? "border-primary-500 bg-primary-500/15 scale-[1.02]"
								: validationResult && !validationResult.valid
									? "border-red-400 bg-red-500/12"
									: selectedFile
										? "border-green-500 bg-green-500/12"
										: "border-strong surface-alt hover:border-primary-300 soft-hover"
						}`}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
					>
						{selectedFile ? (
							<div className="space-y-6">
								<div className="flex items-center justify-center">
									<div className="bg-green-100 rounded-full p-4">
										<CheckCircle className="w-10 h-10 text-green-600" />
									</div>
								</div>
								<div>
									<div className="flex items-center justify-center gap-3 mb-3">
										<File className="w-6 h-6 text-muted" />
										<span className="text-lg font-semibold text-primary">{selectedFile.name}</span>
										<button
											type="button"
											onClick={handleRemoveFile}
											className="ml-2 p-2 soft-hover rounded-full transition-colors"
											aria-label="Remove file"
										>
											<X className="w-5 h-5 text-subtle" />
										</button>
									</div>
									<p className="text-sm text-subtle">{formatFileSize(selectedFile.size)}</p>
								</div>
							</div>
						) : (
							<div className="space-y-6">
								<div className="flex justify-center">
									<div className="surface-muted rounded-full p-6">
										<Upload className="w-12 h-12 text-subtle" />
									</div>
								</div>
								<div>
									<h3 className="text-xl font-semibold text-primary mb-2">Drop your document here</h3>
									<p className="text-sm text-muted mb-6">or click to browse files</p>
									<button type="button" onClick={handleBrowseClick} className="btn btn-primary btn-lg px-8 py-3">
										Browse Files
									</button>
								</div>
								<p className="text-xs text-subtle">Supported formats: PDF, DOCX, TXT, RTF, MD (Max 50MB)</p>
							</div>
						)}
					</div>

					<input
						ref={fileInputRef}
						type="file"
						className="hidden"
						accept=".pdf,.docx,.txt,.rtf,.md"
						onChange={handleFileInputChange}
					/>

					{/* Validation Errors */}
					{validationResult && !validationResult.valid && (
						<div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
							<div className="flex items-start space-x-2">
								<AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
								<div className="flex-1">
									<h4 className="font-medium text-red-900 mb-1">File Validation Errors</h4>
									<ul className="list-disc list-inside text-sm text-red-700 space-y-1">
										{validationResult.errors.map((error) => (
											<li key={error}>{error}</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					)}

					{/* Processing Options */}
					{selectedFile && validationResult?.valid && (
						<div className="mt-8 pt-8 border-t border-subtle space-y-6">
							<h3 className="text-lg font-semibold text-primary">Processing Options</h3>
							<div className="space-y-4">
								<label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg soft-hover transition-colors">
									<input
										type="checkbox"
										checked={uploadOptions.extractEntities}
										onChange={(e) => toggleOption("extractEntities", e.target.checked)}
										className="w-5 h-5 text-primary-600 border-strong surface rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
									/>
									<span className="text-sm font-medium text-muted">Extract Entities</span>
								</label>
								<label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg soft-hover transition-colors">
									<input
										type="checkbox"
										checked={uploadOptions.extractRelationships}
										onChange={(e) => toggleOption("extractRelationships", e.target.checked)}
										className="w-5 h-5 text-primary-600 border-strong surface rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
									/>
									<span className="text-sm font-medium text-muted">Extract Relationships</span>
								</label>
								<label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg soft-hover transition-colors">
									<input
										type="checkbox"
										checked={uploadOptions.generateFormal}
										onChange={(e) => toggleOption("generateFormal", e.target.checked)}
										className="w-5 h-5 text-primary-600 border-strong surface rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
									/>
									<span className="text-sm font-medium text-muted">Generate Formal Expressions</span>
								</label>
								<label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg soft-hover transition-colors">
									<input
										type="checkbox"
										checked={uploadOptions.validateExpressions}
										onChange={(e) => toggleOption("validateExpressions", e.target.checked)}
										className="w-5 h-5 text-primary-600 border-strong surface rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
									/>
									<span className="text-sm font-medium text-muted">Validate Expressions</span>
								</label>
							</div>
						</div>
					)}

					{/* Upload Button */}
					{selectedFile && validationResult?.valid && (
						<div className="mt-8 pt-8 border-t border-subtle flex items-center justify-end gap-4">
							<button
								type="button"
								onClick={handleBack}
								className="btn btn-secondary px-6 py-2.5"
								disabled={uploadMutation.isPending}
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleUpload}
								disabled={uploadMutation.isPending}
								className="btn btn-primary px-6 py-2.5 flex items-center gap-2"
							>
								{uploadMutation.isPending ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										<span>Uploading...</span>
									</>
								) : (
									<>
										<Upload className="w-5 h-5" />
										<span>Upload & Process</span>
									</>
								)}
							</button>
						</div>
					)}

					{/* Upload Error */}
					{uploadMutation.isError && (
						<div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
							<div className="flex items-start space-x-2">
								<AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
								<div>
									<h4 className="font-medium text-red-900 mb-1">Upload Failed</h4>
									<p className="text-sm text-red-700">
										{uploadMutation.error instanceof Error
											? uploadMutation.error.message
											: "An error occurred during upload. Please try again."}
									</p>
								</div>
							</div>
						</div>
					)}
				</div>
			</main>
		</div>
	);
};

export default UploadPage;
